import style from "./style.module.scss";
import classnames from "classnames";
import React, { ReactNode } from "react";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import relativeUnitToAbsolute from "@jmc/solid-design-system/src/utils/relativeUnitToAbsolute";
import { Colors } from "../Typography/Typography";
import { Color } from "./enum/Color.enum";
import { ImagePosition } from "./enum/ImagePosition.enum";
import { Size } from "./enum/Size.enum";
import cleanCSSIdentifier from "@jmc/utils/utils/clean-css-identifier";

export type ContentAlignment = "left" | "center" | "right";
export type ContentVerticalAlignment = "top" | "center" | "bottom";

interface Props {
    backgroundColor?: "primary" | "secondary" | "transparent" | "accent" | "accent2" | "white" | "dark";
    children?: ReactNode;
    contentAlignment?: ContentAlignment;
    contentVerticalAlignment?: ContentVerticalAlignment;
    footer?: JSX.Element | JSX.Element[];
    fullWidth?: boolean;
    hasOverlay?: boolean;
    height?: string;
    id: string;
    image?: string;
    imagePosition?: ImagePosition;
    linkColor?: Color;
    marginXl?: boolean;
    mobilePadding?: boolean;
    overlay?: "none" | "dark" | "light";
    titleVariant?: "h1" | "h2" | "h3";
    size?: Size;
    subtitleColor?: Color;
    textColor?: string; //the name of a css variable with --, ex. --primary
    title?: JSX.Element | string;
    titleColor?: string; //the name of a css variable with --, ex. --primary
    titleComponent?: React.ElementType;
    visitedLinkColor?: string; //hex code
    wideText?: boolean;
    overflow?: boolean;
}

export const Hero = ({
    backgroundColor = "primary",
    children,
    contentAlignment = "center",
    contentVerticalAlignment = "center",
    footer,
    fullWidth = false,
    hasOverlay = false,
    height = null,
    id,
    image,
    imagePosition = ImagePosition.CENTER_CENTER,
    linkColor = Color.DEFAULT,
    marginXl = false,
    mobilePadding = false,
    overlay = "none",
    titleVariant = "h3",
    size = Size.AUTO,
    subtitleColor = Color.DEFAULT,
    textColor = "white",
    title,
    titleColor = "white",
    titleComponent = "h3",
    visitedLinkColor = "default",
    wideText = false,
    overflow = false,
}: Props) => {
    const getMinHeight = () => {
        switch (size) {
            case Size["X-Small"]:
                return relativeUnitToAbsolute("15vh");
            case Size.Small:
                return relativeUnitToAbsolute("30vh");
            case Size.Medium:
                return relativeUnitToAbsolute("45vh");
            case Size.Large:
                return relativeUnitToAbsolute("70vh");
            default:
                return relativeUnitToAbsolute("auto");
        }
    };

    /* TODO: Remove height prop (L21 & L48) and check on L89.
     *  Heroes placed in the page header will use the size defined in the Page header
     *  This is a temporary solution to allow content editors to set everything right
     *  Based on ticket AHAY-2556
     */

    const styleObject = {
        minHeight: height || getMinHeight(),
        height: "auto",
        backgroundBlendMode: "normal",
    } as React.CSSProperties;

    if (image) {
        styleObject.background = `no-repeat center center / cover url('${image}')`;
    }

    //hasOverlay and image
    if (hasOverlay && image) {
        styleObject.background = `${styleObject.background} rgba(20, 20, 20, 0.38)`;
        styleObject.backgroundBlendMode = "multiply";
    }
    //overlay dark and image
    if (overlay && overlay === "dark") {
        styleObject.background = `${image ? styleObject.background : ""} rgba(34, 34, 34, 0.3)`;
        styleObject.backgroundBlendMode = "multiply";
    }
    //overlay light and image
    if (overlay && overlay === "light") {
        styleObject.background = `${image ? styleObject.background : ""} rgba(255, 255, 255, 0.3)`;
        styleObject.backgroundBlendMode = "multiply";
    }

    return (
        <div
            className={classnames(
                style.hero,
                overflow ? style.hero__overflow : null,
                fullWidth && style.hero__fullWidth,
                marginXl && fullWidth && style.hero__marginXl,
                style[`hero__imagePosition__${imagePosition}`],
                style[`hero__backgroundColor__${backgroundColor.replace("--", "")}`],
                mobilePadding ? style.hero__mobilePadding : "",
                wideText ? style.hero__wideText : "",
                style.hero__alignment,
                style[`hero__alignment__horizontal_${contentAlignment}`],
                style[`hero__alignment__vertical_${contentVerticalAlignment}`],
            )}
            style={styleObject}
            data-test-id={`Hero.${id}`}
            id={`Hero.${cleanCSSIdentifier(id?.toLowerCase())}`}
        >
            <div data-test-id="Hero.Content" className={style.content}>
                {title && (
                    <div className={classnames(style.hero__title)} data-test-id={"Hero.Title"}>
                        <span
                            style={
                                {
                                    "--text-primary": `var(${titleColor})`,
                                    ...(linkColor !== "default" && { "--text-link": `var(${linkColor})` }),
                                } as React.CSSProperties
                            }
                        >
                            <Typography
                                variant={titleVariant}
                                color={titleColor ? (titleColor.replace("--", "") as Colors) : "white"}
                                component={titleComponent}
                            >
                                <span style={{ color: "inherit" }}>{title}</span>
                            </Typography>
                        </span>
                    </div>
                )}
                {children && (
                    <div
                        className={classnames(
                            style.hero__description,
                            subtitleColor !== "default" ? style.hero__description_subtitle_color : "",
                            linkColor !== "default" ? style.hero__description_link_color : "",
                        )}
                        data-test-id={"Hero.Description"}
                    >
                        <span
                            style={
                                {
                                    "--text-primary": `var(${textColor})`,
                                    "--hero-subtitle":
                                        subtitleColor !== "default" ? `var(${subtitleColor})` : "default",
                                    ...(linkColor !== "default" && { "--text-link": `var(${linkColor})` }),
                                    "--text-link-visited":
                                        visitedLinkColor !== "default" ? visitedLinkColor : "var(--text-link)",
                                } as React.CSSProperties
                            }
                        >
                            <Typography
                                variant="body"
                                color={textColor ? (textColor.replace("--", "") as Colors) : "white"}
                                component="div"
                            >
                                <span style={{ color: "inherit" }}>{children}</span>
                            </Typography>
                        </span>
                    </div>
                )}
                {(Array.isArray(footer) ? footer.length > 0 : !!footer) && (
                    <div
                        className={classnames(style.hero__footer, style[`hero__footer__${contentAlignment}`])}
                        data-test-id={"Hero.Footer"}
                    >
                        {footer}
                    </div>
                )}
            </div>
        </div>
    );
};
